import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PlusOutlined, MinusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { OrderItemEditor as OrderItem } from "../model/types";
import {
  AutoComplete,
  Card,
  Select,
  Input,
  Button,
  Spin,
  Typography,
  Divider,
} from "antd";

import {
  fetchProductAttributeOptions,
  fetchShippersByProductAndOrigin,
} from "../../../entities/product/api/productsApi";
import { fetchOrderUnitOptions } from "../api/ordersApi";
import type { CursorPaginatedResponse } from "../../../shared/api/pagination";
import { PriceInput } from "../../../shared/components/PriceInput";
import { formatWholePrice } from "../../../shared/utils/price";

const { Text } = Typography;

type OptionType = {
  value: number | string;
  label: string;
  code?: string;
};

type SuggestionState = {
  options: OptionType[];
  search: string;
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
};

const EMPTY_SUGGESTIONS: SuggestionState = {
  options: [],
  search: "",
  cursor: null,
  hasMore: false,
  loading: false,
};

const toOptions = (values: string[]): OptionType[] =>
  values.map((item) => ({ value: item, label: item }));

const renderSuggestionPopup =
  (loading: boolean, hasOptions: boolean) => (menu: React.ReactElement) => (
    <>
      {menu}
      {loading && hasOptions && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 12px",
            color: "#6b7280",
          }}
        >
          <Spin size="small" style={{ color: "#6b7280" }} />
        </div>
      )}
    </>
  );

function useInfiniteSuggestions(
  loadPage: (
    search: string,
    cursor?: string,
  ) => Promise<CursorPaginatedResponse<string>>,
  enabled: boolean,
) {
  const [state, setState] = useState<SuggestionState>(EMPTY_SUGGESTIONS);
  const stateRef = useRef(state);
  const timerRef = useRef<NodeJS.Timeout>();
  const requestIdRef = useRef(0);

  const replaceState = useCallback((nextState: SuggestionState) => {
    stateRef.current = nextState;
    setState(nextState);
  }, []);

  const load = useCallback(
    async (search: string, append: boolean) => {
      const normalizedSearch = search.trim();
      const previous = stateRef.current;
      if (!enabled || (append && (!previous.hasMore || previous.loading)))
        return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      replaceState({
        ...(append ? previous : EMPTY_SUGGESTIONS),
        loading: true,
      });

      try {
        const response = await loadPage(
          append ? previous.search : normalizedSearch,
          append ? previous.cursor || undefined : undefined,
        );
        if (requestId !== requestIdRef.current) return;

        const received = toOptions(response.items);
        const options = append
          ? [
              ...previous.options,
              ...received.filter(
                (option) =>
                  !previous.options.some(
                    (current) => current.value === option.value,
                  ),
              ),
            ]
          : received;
        replaceState({
          options,
          search: append ? previous.search : normalizedSearch,
          cursor: response.next_cursor,
          hasMore: response.has_more,
          loading: false,
        });
      } catch (error) {
        if (requestId === requestIdRef.current) {
          console.error("Error loading suggestions", error);
          replaceState({
            ...(append ? previous : EMPTY_SUGGESTIONS),
            loading: false,
          });
        }
      }
    },
    [enabled, loadPage, replaceState],
  );

  const search = useCallback(
    (value: string) => {
      requestIdRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void load(value, false);
      }, 300);
    },
    [load],
  );

  const onPopupScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const dropdown = event.currentTarget;
      const remaining =
        dropdown.scrollHeight - dropdown.scrollTop - dropdown.clientHeight;
      if (remaining < 48) void load(stateRef.current.search, true);
    },
    [load],
  );

  const onFocus = useCallback(() => {
    if (!stateRef.current.loading && stateRef.current.options.length === 0)
      search("");
  }, [search]);

  useEffect(() => {
    requestIdRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    replaceState(EMPTY_SUGGESTIONS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, loadPage, replaceState]);

  return {
    ...state,
    onFocus,
    onPopupScroll,
    onSearch: search,
  };
}

type Props = {
  value: any;
  onChange: (v: any) => void;
  onDelete?: () => void;
  distributorOptions?: OptionType[];

  productCode?: string | number;
  productName?: string;
  itemNumber: number;
  isSingle?: boolean;
  productOptions?: OptionType[];
  onSearchProduct?: (search: string) => Promise<void>;
  productLoading?: boolean;
  readOnly?: boolean;
  sourceOrder?: string;
  leadTimeError?: string;
};

const OrderItemCard: React.FC<Props> = ({
  value,
  onChange,
  onDelete,
  productCode,
  distributorOptions,
  productName,
  itemNumber,
  isSingle = true,
  productOptions = [],
  onSearchProduct,
  productLoading,
  readOnly = false,
  sourceOrder,
  leadTimeError,
}) => {
  const update = (field: keyof OrderItem, val: any) => {
    onChange({ ...value, [field]: val });
  };

  const originLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchProductAttributeOptions(productCode!.toString(), "production", {
        search,
        cursor,
      }),
    [productCode],
  );
  const originSuggestions = useInfiniteSuggestions(
    originLoader,
    Boolean(productCode) && !readOnly,
  );
  const gradeLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchProductAttributeOptions(productCode!.toString(), "grade", {
        search,
        cursor,
      }),
    [productCode],
  );
  const gradeSuggestions = useInfiniteSuggestions(
    gradeLoader,
    Boolean(productCode) && !readOnly,
  );
  const sizeLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchProductAttributeOptions(productCode!.toString(), "class", {
        search,
        cursor,
      }),
    [productCode],
  );
  const sizeSuggestions = useInfiniteSuggestions(
    sizeLoader,
    Boolean(productCode) && !readOnly,
  );
  const packagingLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchProductAttributeOptions(productCode!.toString(), "packaging", {
        search,
        cursor,
      }),
    [productCode],
  );
  const packagingSuggestions = useInfiniteSuggestions(
    packagingLoader,
    Boolean(productCode) && !readOnly,
  );
  const shipperLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchShippersByProductAndOrigin(
        productCode!.toString(),
        value.origin_code!,
        {
          search,
          cursor,
        },
      ).then((response) => ({
        ...response,
        items: response.items.map((shipper) => shipper.shipper_code),
      })),
    [productCode, value.origin_code],
  );
  const shipperSuggestions = useInfiniteSuggestions(
    shipperLoader,
    Boolean(productCode && value.origin_code) && !readOnly,
  );
  const unitLoader = useCallback(
    (search: string, cursor?: string) =>
      fetchOrderUnitOptions({ search, cursor }),
    [],
  );
  const unitSuggestions = useInfiniteSuggestions(unitLoader, !readOnly);

  const q = value.order_quantity || 0;

  const displayNotes = useMemo(() => {
    if (!readOnly) return value.notes || "";
    const parts = [
      value.daily_note?.trim(),
      value.weekly_note?.trim(),
      value.notes?.trim(),
    ].filter(Boolean);
    return parts.join(" . ");
  }, [readOnly, value.daily_note, value.weekly_note, value.notes]);

  return (
    <Card className="order-card">
      {/* ===== HEADER ===== */}
      {!isSingle && !readOnly && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 8,
          }}
        >
          <DeleteOutlined className="delete" onClick={onDelete} />
        </div>
      )}
      <div className="card-header" style={{ alignItems: "center" }}>
        <div
          className="header-left"
          style={{ width: !isSingle ? "100%" : "auto" }}
        >
          {isSingle && <div className="icon">🌿</div>}
          <div style={{ flex: 1, marginRight: isSingle ? 16 : 0 }}>
            {isSingle ? (
              <>
                <div className="title">
                  商品名 {itemNumber}: {productName || "-"}
                </div>
                {!readOnly && (
                  <div className="sub">商品コード: {productCode}</div>
                )}
              </>
            ) : readOnly ? (
              <div className="field">
                <label
                  style={{ color: "#6b7280", fontSize: 13, marginBottom: 4 }}
                >
                  商品名 {itemNumber}
                </label>
                <Input
                  value={value.product_name || "-"}
                  readOnly
                />
              </div>
            ) : (
              <div className="field">
                <label
                  style={{ color: "#6b7280", fontSize: 13, marginBottom: 4 }}
                >
                  商品名 {itemNumber}
                </label>
                <Select
                  style={{ width: "100%", minWidth: 0 }}
                  status={leadTimeError ? "error" : undefined}
                  showSearch
                  value={value.product_code}
                  onChange={(v) => {
                    const opt = productOptions.find((p) => p.value === v);
                    onChange({
                      ...value,
                      product_code: v as string,
                      product_name: opt?.label?.split(" - ")[1],
                      origin_code: undefined,
                      origin_id: undefined,
                      origin_name: undefined,
                      shipper_id: undefined,
                      shipper_name: undefined,
                      shipper_code: undefined,
                      grade: undefined,
                      rank: undefined,
                      package_type: undefined,
                    });
                  }}
                  options={productOptions}
                  onSearch={onSearchProduct}
                  filterOption={false}
                  loading={productLoading}
                  allowClear
                  placeholder="商品を選択してください"
                  notFoundContent="該当データなし"
                />
                {leadTimeError && (
                  <Text type="danger" className="lead-time-field-error">
                    {leadTimeError}
                  </Text>
                )}
              </div>
            )}
          </div>
        </div>

        {isSingle && !readOnly && (
          <DeleteOutlined className="delete" onClick={onDelete} />
        )}
      </div>

      {/* ===== ORIGIN + SHIPPER ===== */}
      <div className="row">
        <div className="field">
          <label>産地</label>
          {readOnly ? (
            <Input value={value.origin_name || ""} readOnly />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.origin_code}
              onFocus={originSuggestions.onFocus}
              onSearch={originSuggestions.onSearch}
              onPopupScroll={originSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                originSuggestions.loading,
                originSuggestions.options.length > 0,
              )}
              onChange={(v) => {
                onChange({
                  ...value,
                  origin_code: v || undefined,
                  origin_name: v || undefined,
                  origin_id: undefined,
                  shipper_id: undefined,
                  shipper_name: undefined,
                  shipper_code: undefined,
                });
              }}
              options={originSuggestions.options}
              allowClear
              placeholder="選択してください"
              notFoundContent={
                originSuggestions.loading ? "読み込み中..." : "該当データなし"
              }
            />
          )}
        </div>

        <div className="field">
          <label>出荷者</label>
          {readOnly ? (
            <Input value={value.shipper_name || ""} readOnly />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.shipper_code}
              onFocus={shipperSuggestions.onFocus}
              onSearch={shipperSuggestions.onSearch}
              onPopupScroll={shipperSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                shipperSuggestions.loading,
                shipperSuggestions.options.length > 0,
              )}
              onChange={(v) => {
                onChange({
                  ...value,
                  shipper_code: v || undefined,
                  shipper_name: v || undefined,
                });
              }}
              options={shipperSuggestions.options}
              allowClear
              placeholder="選択してください"
              notFoundContent={
                shipperSuggestions.loading ? "読み込み中..." : "該当データなし"
              }
            />
          )}
        </div>
      </div>

      <Divider />

      <div className="row">
        <div className="field">
          <label>等級</label>
          {readOnly ? (
            <Input value={value.grade || ""} readOnly />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.grade}
              onFocus={gradeSuggestions.onFocus}
              onSearch={gradeSuggestions.onSearch}
              onPopupScroll={gradeSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                gradeSuggestions.loading,
                gradeSuggestions.options.length > 0,
              )}
              onChange={(v) => update("grade", v || undefined)}
              options={gradeSuggestions.options}
              allowClear
              notFoundContent={
                gradeSuggestions.loading ? "読み込み中..." : "該当データなし"
              }
              placeholder="選択してください"
            />
          )}
        </div>

        <div className="field">
          <label>階級</label>
          {readOnly ? (
            <Input value={value.rank || ""} readOnly />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.rank}
              onFocus={sizeSuggestions.onFocus}
              onSearch={sizeSuggestions.onSearch}
              onPopupScroll={sizeSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                sizeSuggestions.loading,
                sizeSuggestions.options.length > 0,
              )}
              onChange={(v) => update("rank", v || undefined)}
              options={sizeSuggestions.options}
              allowClear
              notFoundContent={
                sizeSuggestions.loading ? "読み込み中..." : "該当データなし"
              }
              placeholder="選択してください"
            />
          )}
        </div>
      </div>

      <Divider />

      {/* ===== UNIT + WEIGHT ===== */}
      <div className="row">
        <div className="field">
          <label>注文単位</label>
          {readOnly ? (
            <Input value={value.order_unit || ""} readOnly />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.order_unit}
              onFocus={unitSuggestions.onFocus}
              onSearch={unitSuggestions.onSearch}
              onPopupScroll={unitSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                unitSuggestions.loading,
                unitSuggestions.options.length > 0,
              )}
              onChange={(v) => update("order_unit", v || undefined)}
              options={unitSuggestions.options}
              allowClear
              filterOption={false}
              notFoundContent={
                unitSuggestions.loading ? "読み込み中..." : "該当データなし"
              }
              placeholder="選択または入力してください"
            />
          )}
        </div>

        <div className="field">
          <label>量目</label>
          {readOnly ? (
            <Input value={value.weight || ""} readOnly />
          ) : (
            <Input
              value={value.weight}
              onChange={(e) => update("weight", e.target.value)}
              placeholder="10 dkg"
              readOnly={readOnly}
            />
          )}
        </div>
      </div>

      {/* ===== QUANTITY ===== */}
      <div className="qty-box">
        <Text strong>注文数量</Text>

        <div
          className="qty-control"
          style={{ display: "flex", alignItems: "center", gap: "20px" }}
        >
          {!readOnly && (
            <Button
              className="btn-minus"
              shape="circle"
              icon={<MinusOutlined />}
              onClick={() => update("order_quantity", Math.max(0, q - 1))}
            />
          )}

          {readOnly ? (
            <Input
              className="qty"
              value={value.order_quantity || 0}
              readOnly
              style={{
                width: "80px",
                textAlign: "center",
                margin: "0 8px",
                height: "36px",
              }}
            />
          ) : (
            <Input
              type="number"
              className="qty"
              value={q}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 5) {
                  update("order_quantity", parseInt(value) || 0);
                }
              }}
              onInput={(e: React.FormEvent<HTMLInputElement>) => {
                if (e.currentTarget.value.length > 5) {
                  e.currentTarget.value = e.currentTarget.value.slice(0, 5);
                }
              }}
              style={{
                width: "80px",
                textAlign: "center",
                margin: "0 8px",
                height: "36px",
              }}
            />
          )}

          {!readOnly && (
            <Button
              style={{ backgroundColor: "#10b981" }}
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              onClick={() => update("order_quantity", q + 1)}
            />
          )}
        </div>

        {!readOnly && (
          <div className="quick">
            <Button onClick={() => update("order_quantity", q + 10)}>
              + 10
            </Button>
            <Button onClick={() => update("order_quantity", q + 20)}>
              + 20
            </Button>
            <Button onClick={() => update("order_quantity", q + 50)}>
              + 50
            </Button>
          </div>
        )}
      </div>

      <Divider />

      {/* ===== PACKAGE ===== */}
      <div className="row">
        <div className="field">
          <label>荷姿</label>
          {readOnly ? (
            <Input
              className="package"
              value={value.package_type || ""}
              readOnly
            />
          ) : (
            <AutoComplete
              style={{ width: "100%" }}
              value={value.package_type}
              onFocus={packagingSuggestions.onFocus}
              onSearch={packagingSuggestions.onSearch}
              onPopupScroll={packagingSuggestions.onPopupScroll}
              popupRender={renderSuggestionPopup(
                packagingSuggestions.loading,
                packagingSuggestions.options.length > 0,
              )}
              onChange={(v) => update("package_type", v || undefined)}
              options={packagingSuggestions.options}
              notFoundContent={
                packagingSuggestions.loading
                  ? "読み込み中..."
                  : "該当データなし"
              }
              placeholder="選択してください"
              allowClear
            />
          )}
        </div>

        <div className="field">
          <label>入数</label>
          {readOnly ? (
            <Input value={value.quantity_per_package || ""} readOnly />
          ) : (
            <Input
              type="number"
              value={value?.quantity_per_package || undefined}
              onChange={(e) =>
                update("quantity_per_package", Number(e.target.value))
              }
              readOnly={readOnly}
            />
          )}
        </div>
      </div>

      <Divider />

      {/* ===== PRICE ===== */}
      <div className="field">
        <label>提案価格 (円)</label>
        {readOnly ? (
          <Input value={formatWholePrice(value?.proposed_price)} readOnly />
        ) : (
          <PriceInput
            value={value?.proposed_price}
            onChange={(nextPrice) => update("proposed_price", nextPrice)}
          />
        )}
      </div>

      {sourceOrder !== "order" && (
        <div className="field" style={{ marginTop: "16px" }}>
          <label>備考</label>
          <Input.TextArea
            placeholder="特記事項があれば入力..."
            value={displayNotes}
            onChange={(e) => update("notes", e.target.value)}
            autoSize={{ minRows: 3, maxRows: 5 }}
            readOnly={readOnly}
          />
        </div>
      )}
    </Card>
  );
};

export default OrderItemCard;
